// dental-referral-form.component.ts
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { AppService, DentalBidirectionalList, SavePCPReferralRequest, GetDentalBidirectionalListsRequest, MemberCCOEnrollment, DentalReferralFormReview, PCPReferral, MemberMasterDimLite } from './../app.service';
import { catchError, finalize, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AlertService } from '../common/alert.service';
import { LoadingService } from '../common/loading.service';
import { AuthService } from '../auth/services/auth.service';
import { LogoutService } from '../auth/services/logout.service';
import { CommonFunctions } from '../common/common-functions';

@Component({
  selector: 'app-dental-referral-form',
  templateUrl: './dental-referral-form.component.html',
  styleUrls: ['./dental-referral-form.component.scss']
})
export class DentalReferralFormComponent implements OnInit {
  public formLists: DentalBidirectionalList[] = [];
  public form: FormGroup = new FormGroup({});
  public formReady = false; // <- use in template: *ngIf="formReady"
  public todaysDate: string = new Date().toLocaleDateString();

  public memberInfo: MemberCCOEnrollment = new MemberCCOEnrollment();
  public memberMasterDimLite: MemberMasterDimLite | null = null;
  public memberSearchMessage: string = '';
  public memberSearchNotFound: boolean = false;

  public isFormSubmitted: boolean = false;

  public today = new Date(); // used for dob date picker field
  userEmail?: string;
  userProviderNPI?: string;
  userProviderID?: string;
  userDisplayName?: string;
  providerFirstName?: string;
  providerLastName?: string;
  facilityName?: string;

  public dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const earliest = new Date(1900, 0, 1);
    earliest.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date >= earliest && date <= this.today;
  };

  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private authService: AuthService,
    private logoutService: LogoutService,
    private common: CommonFunctions
  ) { }

  async ngOnInit() {
    // 1) Build the full form structure FIRST so template bindings always find controls
    this.initForm();

    // 2) Load user and provider context (safe-guard for null/undefined)
    await this.authService.getCurrentUser();
    const ctx = this.authService.getProviderContext(); // may be undefined for Azure AD / DCO users

    this.userEmail = this.authService.getUserEmail() ?? undefined;
    this.userDisplayName = this.authService.getDisplayName() ?? undefined;

    // only read from ctx if it exists
    if (ctx) {
      this.userProviderNPI = ctx.providerNpi ?? undefined;
      this.userProviderID = ctx.providerId ?? undefined;
      this.providerFirstName = ctx.providerFirstName ?? undefined;
      this.providerLastName = ctx.providerLastName ?? undefined;
      this.facilityName = ctx.facilityName ?? undefined;
      this.prefillProviderFromContext();
    }

    this.today.setHours(0, 0, 0, 0);
    this.loadFormLists();

    // set up reactive enable/disable logic
    this.setupOtherClinicalPresentationControl();
    this.setupOtherMedicalConditionsControl();

    this.formReady = true; // let the template render
  }

  private initForm() {
    // Full tree so template can bind immediately
    this.form = new FormGroup({
      urgentRequest: new FormControl('Non-Urgent', Validators.required),
      patientLookup: new FormGroup({
        medicaidId: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(8)
        ])
      }),
      patient: new FormGroup({
        firstName: new FormControl('', Validators.required),
        lastName: new FormControl('', Validators.required),
        dob: new FormControl('', [Validators.required, noFutureDateValidator]),
        phone: new FormControl('', Validators.required),
        parentGuardian: new FormControl(''),
        awareOfRequest: new FormControl('Yes', Validators.required)
      }),
      provider: new FormGroup({
        clinic: new FormControl('', Validators.required),            // Facility/Clinic name
        clinicNPI: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{10}$')]),
        providerName: new FormControl(''),
        phoneNumber: new FormControl('', Validators.required),
        fax: new FormControl(''),
        email: new FormControl('', [Validators.required, Validators.email]),
        submitterName: new FormControl('', Validators.required)
      }),
      clinicalPresentation: new FormGroup({
        dentalCaries: new FormControl(false),
        swellingAbscess: new FormControl(false),
        oralPathology: new FormControl(false),
        pain: new FormControl(false),
        otherClinicalPresentation: new FormControl(false),
        otherClinicalPresentationDescription: new FormControl(''),
      }),
      healthConditions: new FormGroup({
        pregnancy: new FormControl(false),
        diabetes: new FormControl(false),
        cardiovascularDisease: new FormControl(false),
        otherMedicalConditions: new FormControl(false),
        otherMedicalConditionsDescription: new FormControl(''),
      }),
      comments: new FormControl('')
    });
  }

  /** Prefill provider section from HealthTrio context */
  private prefillProviderFromContext() {
    const providerGroup = this.form.get('provider') as FormGroup;
    if (!providerGroup) return;

    // Decide what "clinic" should show: facility name if present, else providerId
    const clinicValue = this.facilityName || this.userProviderID || '';

    providerGroup.patchValue({
      clinic: clinicValue,
      clinicNPI: this.userProviderNPI || '',
      email: this.userEmail || '',
      //providerName: [this.providerFirstName, this.providerLastName].filter(Boolean).join(' '),  //do not populate until HealthTrio sends us prov name in saml response
      submitterName: this.userDisplayName || ''
    });
  }

  private setupOtherClinicalPresentationControl() {
    const otherCheck = this.form.get('clinicalPresentation.otherClinicalPresentation');
    const otherDesc = this.form.get('clinicalPresentation.otherClinicalPresentationDescription');
    if (!otherCheck?.value) otherDesc?.disable();

    otherCheck?.valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        otherDesc?.setValidators([Validators.required]);
        otherDesc?.enable();
      } else {
        otherDesc?.clearValidators();
        otherDesc?.disable();
        otherDesc?.reset();
      }
      otherDesc?.updateValueAndValidity();
    });
  }

  private setupOtherMedicalConditionsControl() {
    const otherMedCheck = this.form.get('healthConditions.otherMedicalConditions');
    const otherMedDesc = this.form.get('healthConditions.otherMedicalConditionsDescription');
    if (!otherMedCheck?.value) otherMedDesc?.disable();

    otherMedCheck?.valueChanges.subscribe((isChecked: boolean) => {
      if (isChecked) {
        otherMedDesc?.setValidators([Validators.required]);
        otherMedDesc?.enable();
      } else {
        otherMedDesc?.clearValidators();
        otherMedDesc?.disable();
        otherMedDesc?.reset();
      }
      otherMedDesc?.updateValueAndValidity();
    });
  }

  private loadFormLists() {
    const request = new GetDentalBidirectionalListsRequest();
    this.spinner.show();

    this.service.GetDentalBidirectionalLists(request).pipe(
      tap((result) => {
        if (result.success) {
          this.formLists = result.lists;
        } else {
          console.log(`ERROR: ${result.message}`);
        }
      }),
      catchError((error) => {
        console.error(error);
        return of([] as any);
      }),
      finalize(() => this.spinner.hide())
    ).subscribe();
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.spinner.show();
      const request = new SavePCPReferralRequest();
      request.pcpReferral = this.GetDentalReferralFormValues();

      this.service.SavePCPReferral(request)
        .pipe(finalize(() => this.spinner.hide()))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.alert.success('Form submitted successfully!');
              this.clearMemberSection();
              this.form.reset();
              this.isFormSubmitted = true;
            } else {
              this.alert.warning(response.message);
            }
          },
          error: (error) => {
            console.error('Error submitting form:', error);
            this.alert.error('Failed to submit the form. Please try again.');
          }
        });
    } else {
      this.form.markAllAsTouched();
      this.alert.warning('Please correct the errors in the form before submitting.');
    }
  }

  GetDentalReferralFormValues(): PCPReferral {
    const referralForm = new PCPReferral();
    referralForm.isUrgent = this.form.get('urgentRequest')?.value === 'Urgent';

    const patientGroup = this.form.get('patient') as FormGroup;
    referralForm.memberFirstName = patientGroup.get('firstName')?.value;
    referralForm.memberLastName = patientGroup.get('lastName')?.value;
    referralForm.memberDob = new Date(patientGroup.get('dob')?.value);
    referralForm.memberPhoneNumber = patientGroup.get('phone')?.value;
    referralForm.memberParentOrGuardianName = patientGroup.get('parentGuardian')?.value;

    const patientLookupGroup = this.form.get('patientLookup') as FormGroup;
    referralForm.memberMedicaidId = patientLookupGroup.get('medicaidId')?.value;

    const providerGroup = this.form.get('provider') as FormGroup;
    referralForm.clinicProgramName = providerGroup.get('clinic')?.value;
    referralForm.clinicNpi = providerGroup.get('clinicNPI')?.value;
    referralForm.providerName = providerGroup.get('providerName')?.value;
    referralForm.providerPhoneNumber = providerGroup.get('phoneNumber')?.value;
    referralForm.providerFaxNumber = providerGroup.get('fax')?.value;
    referralForm.providerEmailAddress = providerGroup.get('email')?.value;
    referralForm.submittedBy = providerGroup.get('submitterName')?.value;

    const clinicalPresentationGroup = this.form.get('clinicalPresentation') as FormGroup;
    referralForm.dentalCariesOrDecay = clinicalPresentationGroup.get('dentalCaries')?.value;
    referralForm.swellingOrAbscess = clinicalPresentationGroup.get('swellingAbscess')?.value;
    referralForm.oralPathology = clinicalPresentationGroup.get('oralPathology')?.value;
    referralForm.pain = clinicalPresentationGroup.get('pain')?.value;
    referralForm.otherClinicalPresentation = clinicalPresentationGroup.get('otherClinicalPresentation')?.value;
    referralForm.otherClinicalPresentationDescription = clinicalPresentationGroup.get('otherClinicalPresentationDescription')?.value;

    const healthConditionsGroup = this.form.get('healthConditions') as FormGroup;
    referralForm.pregnancy = healthConditionsGroup.get('pregnancy')?.value;
    referralForm.diabetes = healthConditionsGroup.get('diabetes')?.value;
    referralForm.cardiovascularDisease = healthConditionsGroup.get('cardiovascularDisease')?.value;
    referralForm.otherMedicalConditions = healthConditionsGroup.get('otherMedicalConditions')?.value;
    referralForm.otherMedicalConditionsDescription = healthConditionsGroup.get('otherMedicalConditionsDescription')?.value;

    referralForm.commentsAboutCondition = this.form.get('comments')?.value;

    referralForm.guardianName = 'Test';
    referralForm.referralType = 'PCP';

    referralForm.review = new DentalReferralFormReview();
    referralForm.review.referralType = 'PCP';
    referralForm.review.notes = [];

    // Member info from search (not displayed in form)
    if (this.memberMasterDimLite) {
      referralForm.memberMailingAddress1 = this.memberMasterDimLite.mailingAddressLine1;
      referralForm.memberMailingAddress2 = this.memberMasterDimLite.mailingAddressLine2;
      referralForm.memberMailingCity = this.memberMasterDimLite.mailingCity;
      referralForm.memberMailingState = this.memberMasterDimLite.mailingStateCode;
      referralForm.memberMailingZip = this.memberMasterDimLite.mailingZipCode;
      referralForm.memberMailingCounty = this.memberMasterDimLite.mailingCounty;
      referralForm.memberLanguageSpoken = this.memberMasterDimLite.languageSpoken;
      referralForm.memberLanguageWritten = this.memberMasterDimLite.languageWritten;
      
    }

    if (this.memberMasterDimLite) {
      referralForm.cco = this.memberInfo.cco;
    }

    return referralForm;
  }

  clearMemberSection(): void {
    this.memberSearchMessage = '';
    this.memberSearchNotFound = false;

    this.form.patchValue({
      patient: { firstName: '', lastName: '', dob: '', phone: '' }
    });
  }

  onPhoneInput(event: any): void {
    const input = event.target.value.replace(/\D/g, '');
    // Correct path – 'phone' lives under patient.*
    this.form.get('patient.phone')?.setValue(input, { emitEvent: false });
  }

  getMemberData(): void {
    this.clearMemberSection();
    const medicaidIdControl = this.form.get('patientLookup.medicaidId');

    if (medicaidIdControl?.valid) {
      const medicaidId = medicaidIdControl.value;
      this.spinner.show();

      this.service.GetMemberData({ memberId: medicaidId })
        .pipe(finalize(() => this.spinner.hide()))
        .subscribe({
          next: (response) => {
            if (response.success) {
              if (response.isMemberFound) {
                this.memberInfo = response.member;
                this.memberMasterDimLite = response.memberMasterDimLite;

                this.form.patchValue({
                  patient: {
                    firstName: this.memberInfo.firstName || '',
                    lastName: this.memberInfo.lastName || '',
                    dob: this.common.toLocalDate(this.memberInfo.dob) || '',
                    phone: this.memberInfo.phoneNumber || ''
                  }
                });

                if (!response.isMemberActive) {
                  this.memberSearchMessage = `Member ${medicaidIdControl.value} was found but does not have an active dental plan.`;
                  this.alert.info(this.memberSearchMessage);
                  medicaidIdControl?.markAsTouched();
                } else {
                  this.memberSearchMessage = '';
                }
              } else {
                this.memberSearchMessage = `Member ${medicaidIdControl.value} was not found. Please try again or enter Patient Information manually.`;
                this.alert.warning(this.memberSearchMessage);
                this.memberSearchNotFound = true;
                medicaidIdControl?.markAsTouched();
              }
            } else {
              this.alert.error(response.message);
            }
          },
          error: (error) => {
            console.error('Error fetching member data', error);
            this.alert.error('Error fetching member data.');
          }
        });
    } else {
      medicaidIdControl?.markAsTouched();
    }
  }

  onLogoutClick(): void {
    this.logoutService.confirmAndLogout();
  }

  startNewForm(): void {
    this.isFormSubmitted = false;
    this.initForm();
    // optionally re-prefill after reset
    this.prefillProviderFromContext();
  }
}

export function noFutureDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  const selected = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  selected.setHours(0, 0, 0, 0);

  return selected > today ? { futureDateNotAllowed: true } : null;
}
